import { useEffect, useState } from "react";

import { purchaseService } from "~/services/purchase-service";
import {
  purchaseBillingRank,
  purchaseErrorMessage,
  purchasePlanRank,
  type PurchasePackage,
  type PurchaseState,
} from "~/services/purchases";

const initialState: PurchaseState = {
  configured: false,
  loading: false,
  purchasing: false,
  managing: false,
  packages: [],
  customerInfo: null,
  hasMintStintPro: false,
  error: null,
};

function packageSortValue(pkg: PurchasePackage) {
  return (
    purchasePlanRank(pkg.plan) * 10 + purchaseBillingRank(pkg.billingPeriod)
  );
}

export function usePurchases(userId: string | null) {
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    setState((value) => ({ ...value, loading: true, error: null }));
    purchaseService
      .load(userId)
      .then(({ configured, packages, customerInfo }) => {
        if (active) {
          setState({
            configured,
            loading: false,
            purchasing: false,
            managing: false,
            packages: [...packages].sort(
              (a, b) => packageSortValue(a) - packageSortValue(b),
            ),
            customerInfo,
            hasMintStintPro: customerInfo.hasMintStintPro,
            error: null,
          });
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setState((value) => ({
            ...value,
            configured: false,
            loading: false,
            error:
              error instanceof Error ? error.message : "Unable to load plans.",
          }));
        }
      });
    return () => {
      active = false;
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    return purchaseService.addCustomerInfoListener((customerInfo) => {
      setState((value) => ({
        ...value,
        customerInfo,
        hasMintStintPro: customerInfo.hasMintStintPro,
      }));
    });
  }, [userId]);

  const refreshCustomerInfo = async () => {
    const customerInfo = await purchaseService.getCustomerInfo();
    setState((value) => ({
      ...value,
      customerInfo,
      hasMintStintPro: customerInfo.hasMintStintPro,
    }));
    return customerInfo;
  };

  const purchase = async (pkg: PurchasePackage) => {
    setState((value) => ({ ...value, purchasing: true, error: null }));
    try {
      const customerInfo = await purchaseService.purchase(pkg);
      setState((value) => ({
        ...value,
        purchasing: false,
        customerInfo,
        hasMintStintPro: customerInfo.hasMintStintPro,
      }));
      return customerInfo;
    } catch (error) {
      const message = purchaseErrorMessage(
        error,
        "Unable to complete purchase.",
      );
      setState((value) => ({
        ...value,
        purchasing: false,
        error: message,
      }));
      throw error;
    }
  };

  const restore = async () => {
    setState((value) => ({ ...value, purchasing: true, error: null }));
    try {
      const customerInfo = await purchaseService.restore();
      setState((value) => ({
        ...value,
        purchasing: false,
        customerInfo,
        hasMintStintPro: customerInfo.hasMintStintPro,
      }));
      return customerInfo;
    } catch (error) {
      const message = purchaseErrorMessage(
        error,
        "Unable to restore purchases.",
      );
      setState((value) => ({
        ...value,
        purchasing: false,
        error: message,
      }));
      throw error;
    }
  };

  const getManagementUrl = async () => {
    setState((value) => ({ ...value, managing: true, error: null }));
    try {
      return await purchaseService.getManagementUrl();
    } finally {
      setState((value) => ({ ...value, managing: false }));
    }
  };

  const presentPaywall = async () => {
    setState((value) => ({ ...value, purchasing: true, error: null }));
    try {
      const result = await purchaseService.presentPaywall();
      if (result === "purchased" || result === "restored") {
        await refreshCustomerInfo();
      }
      return result;
    } catch (error) {
      const message = purchaseErrorMessage(error, "Unable to open paywall.");
      setState((value) => ({ ...value, error: message }));
      throw error;
    } finally {
      setState((value) => ({ ...value, purchasing: false }));
    }
  };

  const presentPaywallIfNeeded = async () => {
    setState((value) => ({ ...value, purchasing: true, error: null }));
    try {
      const result = await purchaseService.presentPaywallIfNeeded();
      if (result === "purchased" || result === "restored") {
        await refreshCustomerInfo();
      }
      return result;
    } catch (error) {
      const message = purchaseErrorMessage(error, "Unable to open paywall.");
      setState((value) => ({ ...value, error: message }));
      throw error;
    } finally {
      setState((value) => ({ ...value, purchasing: false }));
    }
  };

  const presentCustomerCenter = async () => {
    setState((value) => ({ ...value, managing: true, error: null }));
    try {
      await purchaseService.presentCustomerCenter();
      await refreshCustomerInfo();
    } catch (error) {
      const message = purchaseErrorMessage(
        error,
        "Unable to open subscription management.",
      );
      setState((value) => ({ ...value, error: message }));
      throw error;
    } finally {
      setState((value) => ({ ...value, managing: false }));
    }
  };

  return {
    ...state,
    purchase,
    restore,
    getManagementUrl,
    presentPaywall,
    presentPaywallIfNeeded,
    presentCustomerCenter,
    refreshCustomerInfo,
    supportsNativePaywall: purchaseService.supportsNativePaywall,
    supportsCustomerCenter: purchaseService.supportsCustomerCenter,
  };
}
